// src/components/organisms/AccountForm.tsx
import React, { useState, useMemo } from 'react';
import { Card, ProgressBar, Form, InputGroup, Button as BootstrapButton } from 'react-bootstrap';
import { FaEye, FaEyeSlash, FaCheck, FaTimes } from 'react-icons/fa';
import InputField from '../atoms/InputField';
import Button from '../atoms/Button';
import { User, updateDisplayName, changePassword } from '../../redux/slices/authSlice';
import { useDispatch } from 'react-redux';
import { ThunkDispatch, Action } from '@reduxjs/toolkit';
import { RootState } from '../../redux/rootReducer';
import { fetchWithJwt } from '../../utils/fetchWithJwt';
import { calculatePasswordStrength } from '../../utils/passwordStrength';


interface AccountFormProps {
  user: User;
}

const AccountForm: React.FC<AccountFormProps> = ({ user }) => {
  const dispatch = useDispatch<ThunkDispatch<RootState, void, Action>>();
  const [name, setName] = useState(user.displayName || "--");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Password change state
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const handleSubmit = async () => {
    if (name === user.displayName) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Update profile via API
      await fetchWithJwt('/users/profile', {
        method: 'PUT',
        body: {
          displayName: name,
        },
      });

      // Update Redux state
      dispatch(updateDisplayName(name));
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  // Password strength calculation
  const passwordStrength = useMemo(() => {
    return calculatePasswordStrength(newPassword);
  }, [newPassword]);

  // Password match validation
  const passwordMatch = useMemo(() => {
    return {
      matches: newPassword === confirmPassword && newPassword !== '',
      message: newPassword === confirmPassword ? 'Passwords match' : 'Passwords do not match'
    };
  }, [newPassword, confirmPassword]);

  // Password validation
  const validatePassword = (): boolean => {
    if (!oldPassword) {
      setPasswordError('Old password is required');
      return false;
    }
    if (!newPassword) {
      setPasswordError('New password is required');
      return false;
    }
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return false;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setPasswordError('Password must contain at least one uppercase letter');
      return false;
    }
    if (!/[a-z]/.test(newPassword)) {
      setPasswordError('Password must contain at least one lowercase letter');
      return false;
    }
    if (!/[0-9]/.test(newPassword)) {
      setPasswordError('Password must contain at least one number');
      return false;
    }
    if (!/[^A-Za-z0-9]/.test(newPassword)) {
      setPasswordError('Password must contain at least one special character');
      return false;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handlePasswordChange = async () => {
    setPasswordError(null);

    if (!validatePassword()) {
      return;
    }

    setIsChangingPassword(true);
    try {
      // Redirect to Auth0 for password change
      await dispatch(changePassword());
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Failed to initiate password change');
      setIsChangingPassword(false);
    }
  };

  return (
    <>
      {error && <div className="error" style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}
      {success && <div className="success" style={{ color: 'green', marginBottom: '1rem' }}>Profile updated successfully!</div>}
      
      <Card className="mb-4">
        <Card.Body>
          <Card.Title>Profile Information</Card.Title>
          <InputField
            label="Name"
            type="text"
            name="displayName"
            value={name}
            required
            onChange={(e) => setName(e.target.value)}
          />
          <InputField
            label="Email"
            type="email"
            name="email"
            value={user.email || "--"}
            disabled
            required
            onChange={()=>{}}
          />
          <Button 
            text={isLoading ? "Saving..." : "Save Changes"} 
            type="submit" 
            onClick={handleSubmit}
            disabled={isLoading}
          />
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <Card.Title>Change Password</Card.Title>
          {passwordError && <div className="error" style={{ color: 'red', marginBottom: '1rem' }}>{passwordError}</div>}
          
          <Form.Group className="mb-3">
            <Form.Label>Old Password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showOldPassword ? "text" : "password"}
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                placeholder="Enter your current password"
              />
              <BootstrapButton
                variant="outline-secondary"
                type="button"
                onClick={() => setShowOldPassword(!showOldPassword)}
              >
                {showOldPassword ? <FaEyeSlash /> : <FaEye />}
              </BootstrapButton>
            </InputGroup>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>New Password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter your new password"
              />
              <BootstrapButton
                variant="outline-secondary"
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
              </BootstrapButton>
            </InputGroup>
            <div className="mt-2">
              <ProgressBar 
                now={passwordStrength.score} 
                variant={passwordStrength.variant as 'success' | 'warning' | 'danger' | 'info'}
                label={passwordStrength.label}
                className="mb-1"
              />
              <Form.Text className="text-muted">
                Must be at least 8 characters and include uppercase, lowercase, number, and special character.
              </Form.Text>
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Confirm New Password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
              />
              <BootstrapButton
                variant="outline-secondary"
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </BootstrapButton>
            </InputGroup>
            {confirmPassword && (
              <div className="mt-2 d-flex align-items-center">
                {passwordMatch.matches ? (
                  <FaCheck className="text-success me-2" />
                ) : (
                  <FaTimes className="text-danger me-2" />
                )}
                <Form.Text className={passwordMatch.matches ? "text-success" : "text-danger"}>
                  {passwordMatch.message}
                </Form.Text>
              </div>
            )}
          </Form.Group>

          <Button 
            text={isChangingPassword ? "Redirecting..." : "Change Password"} 
            type="button" 
            onClick={handlePasswordChange}
            disabled={isChangingPassword || !oldPassword || !newPassword || !confirmPassword}
          />
        </Card.Body>
      </Card>
    </>
  );
};

export default AccountForm;